import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AssessmentApiError,
  createAssessment,
  getAssessment,
  updateAssessment,
} from '@/api/assessments/api';
import { fetchSkills, fetchSkillTemplates } from '@/api/skills/api';
import { DEFAULT_STAKEHOLDER_TABLE_MARKDOWN } from '@/components/editor/stakeholderTableMarkdown';
import { writeGuideSelection } from '@/utils/assessmentInterviewFlow';
import {
  BUNDLED_INTERVIEW_GUIDE_TEMPLATE_KEY,
  skillTagsIncludeInterviewGuide,
} from '@/utils/interviewGuideSkillFilter';

/** A selectable guide skill option in the interview prep step's skill picker. */
export interface GuideSkillOption {
  value: string;
  label: string;
}

/** Controls which UI mode is shown in the card modal during interview prep. */
export type InterviewPrepCardModalMode = 'context' | 'create-note';

export interface UseInterviewPrepOptions {
  assessmentInstanceId: string;
  accessToken?: string | null;
  clientId?: string;
  projectId?: string;
  onRunGenerate: () => void;
}

export interface UseInterviewPrepResult {
  isLoading: boolean;
  isSaving: boolean;
  assessmentName: string;
  initialEngagementMarkdown: string;
  initialStakeholderMarkdown: string;
  linkedDocCardIds: string[];
  cardModalOpen: boolean;
  cardModalId: string | null;
  cardModalMode: InterviewPrepCardModalMode;
  browseOpen: boolean;
  guideSkillOptions: GuideSkillOption[];
  selectedGuideSkillValue: string | null;
  skillsLoadLoading: boolean;
  setAssessmentName: (name: string) => void;
  setSelectedGuideSkillValue: (v: string | null) => void;
  linkDocAndOpenModal: (cardId: string) => void;
  openModalForCard: (id: string) => void;
  openCreateNoteModal: (id: string) => void;
  linkDocAndCloseModal: (cardId: string) => void;
  closeCardModal: () => void;
  unlinkDoc: (cardId: string) => void;
  setBrowseOpen: (open: boolean) => void;
  save: (engagementMarkdown: string, stakeholderMarkdown: string) => Promise<void>;
  generate: (engagementMarkdown: string) => void;
}

const DEFAULT_ASSESSMENT_NAME = 'Assessment Title';

const assessmentNameStorageKey = (assessmentInstanceId: string): string =>
  `bdp.interviewPrep.assessmentName.${assessmentInstanceId}`;

const readStoredName = (assessmentInstanceId: string): string => {
  try {
    return (
      sessionStorage.getItem(assessmentNameStorageKey(assessmentInstanceId)) ??
      DEFAULT_ASSESSMENT_NAME
    );
  } catch {
    return DEFAULT_ASSESSMENT_NAME;
  }
};

export function useInterviewPrep({
  assessmentInstanceId,
  accessToken = null,
  clientId,
  projectId,
  onRunGenerate,
}: UseInterviewPrepOptions): UseInterviewPrepResult {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAssessmentId, setSavedAssessmentId] = useState<string | null>(null);
  const [assessmentName, setAssessmentName] = useState(DEFAULT_ASSESSMENT_NAME);
  const [initialEngagementMarkdown, setInitialEngagementMarkdown] = useState('');
  const [initialStakeholderMarkdown, setInitialStakeholderMarkdown] = useState(
    DEFAULT_STAKEHOLDER_TABLE_MARKDOWN,
  );
  const [linkedDocCardIds, setLinkedDocCardIds] = useState<string[]>([]);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cardModalId, setCardModalId] = useState<string | null>(null);
  const [cardModalMode, setCardModalMode] = useState<InterviewPrepCardModalMode>('context');
  const [browseOpen, setBrowseOpen] = useState(false);
  const [guideSkillOptions, setGuideSkillOptions] = useState<GuideSkillOption[]>([]);
  const [selectedGuideSkillValue, setSelectedGuideSkillValue] = useState<string | null>(null);
  const [skillsLoadLoading, setSkillsLoadLoading] = useState(false);

  const bundledOption = useMemo<GuideSkillOption>(
    () => ({
      value: `template:${BUNDLED_INTERVIEW_GUIDE_TEMPLATE_KEY}`,
      label: 'Bundled: Interview Guide Generator',
    }),
    [],
  );

  // Load assessment on mount
  useEffect(() => {
    if (!clientId || !projectId || !accessToken) {
      setAssessmentName(readStoredName(assessmentInstanceId));
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    getAssessment(assessmentInstanceId, accessToken, clientId, projectId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setAssessmentName(data.title || DEFAULT_ASSESSMENT_NAME);
        setInitialEngagementMarkdown(data.engagement_context ?? '');
        setInitialStakeholderMarkdown(
          data.stakeholder_list_markdown ?? DEFAULT_STAKEHOLDER_TABLE_MARKDOWN,
        );
        setLinkedDocCardIds(data.supporting_doc_card_ids ?? []);
        setSavedAssessmentId(assessmentInstanceId);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setAssessmentName(readStoredName(assessmentInstanceId));
        setSavedAssessmentId(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentInstanceId, clientId, projectId, accessToken]);

  // Load available guide skills
  useEffect(() => {
    if (!accessToken) {
      setGuideSkillOptions([bundledOption]);
      setSelectedGuideSkillValue(bundledOption.value);
      setSkillsLoadLoading(false);
      return;
    }

    let cancelled = false;
    setSkillsLoadLoading(true);

    void Promise.all([fetchSkills(accessToken), fetchSkillTemplates(accessToken)])
      .then(([skillsRes, templates]) => {
        if (cancelled) {
          return;
        }
        const next: GuideSkillOption[] = [];
        for (const t of templates) {
          if (skillTagsIncludeInterviewGuide(t.tags)) {
            next.push({ value: `template:${t.template_key}`, label: `${t.title} · template` });
          }
        }
        for (const s of skillsRes.skills) {
          if (skillTagsIncludeInterviewGuide(s.tags)) {
            next.push({ value: `version:${s.id}`, label: `${s.title} · saved skill` });
          }
        }
        if (!next.some((o) => o.value === bundledOption.value)) {
          next.unshift(bundledOption);
        }
        setGuideSkillOptions(next);
        setSelectedGuideSkillValue(next[0]?.value ?? bundledOption.value);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        void message.warning('Could not load skills; using the bundled interview guide template.');
        setGuideSkillOptions([bundledOption]);
        setSelectedGuideSkillValue(bundledOption.value);
      })
      .finally(() => {
        if (!cancelled) {
          setSkillsLoadLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, bundledOption]);

  // Persist assessment name to sessionStorage to survive refresh on new assessments
  useEffect(() => {
    if (isLoading) {
      return;
    }
    try {
      const trimmed = assessmentName.trim();
      if (trimmed) {
        sessionStorage.setItem(assessmentNameStorageKey(assessmentInstanceId), assessmentName);
      } else {
        sessionStorage.removeItem(assessmentNameStorageKey(assessmentInstanceId));
      }
    } catch {
      // ignore quota / private mode
    }
  }, [assessmentName, assessmentInstanceId, isLoading]);

  const linkDocAndOpenModal = useCallback((cardId: string) => {
    setLinkedDocCardIds((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
    setCardModalId(cardId);
    setCardModalMode('context');
    setCardModalOpen(true);
  }, []);

  const openModalForCard = useCallback((id: string) => {
    setCardModalId(id);
    setCardModalMode('context');
    setCardModalOpen(true);
  }, []);

  const openCreateNoteModal = useCallback((id: string) => {
    setCardModalId(id);
    setCardModalMode('create-note');
    setCardModalOpen(true);
  }, []);

  const linkDocAndCloseModal = useCallback((cardId: string) => {
    setLinkedDocCardIds((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
    setCardModalOpen(false);
  }, []);

  const closeCardModal = useCallback(() => {
    setCardModalOpen(false);
  }, []);

  const unlinkDoc = useCallback((cardId: string) => {
    setLinkedDocCardIds((prev) => prev.filter((id) => id !== cardId));
  }, []);

  const save = useCallback(
    async (engagementMarkdown: string, stakeholderMarkdown: string): Promise<void> => {
      if (!clientId || !projectId || !accessToken) {
        return;
      }
      setIsSaving(true);
      try {
        const payload = {
          title: assessmentName.trim() || DEFAULT_ASSESSMENT_NAME,
          engagement_context: engagementMarkdown,
          stakeholder_list_markdown: stakeholderMarkdown,
          supporting_doc_card_ids: linkedDocCardIds,
        };
        if (savedAssessmentId) {
          await updateAssessment(savedAssessmentId, payload, accessToken, clientId, projectId);
        } else {
          try {
            await createAssessment(
              { id: assessmentInstanceId, ...payload },
              accessToken,
              clientId,
              projectId,
            );
          } catch (err) {
            if (err instanceof AssessmentApiError && err.status === 409) {
              await updateAssessment(
                assessmentInstanceId,
                payload,
                accessToken,
                clientId,
                projectId,
              );
            } else {
              throw err;
            }
          }
          setSavedAssessmentId(assessmentInstanceId);
        }
        void message.success('Assessment saved');
      } catch (err) {
        void message.error(err instanceof Error ? err.message : 'Save failed');
      } finally {
        setIsSaving(false);
      }
    },
    [
      clientId,
      projectId,
      accessToken,
      assessmentName,
      linkedDocCardIds,
      savedAssessmentId,
      assessmentInstanceId,
    ],
  );

  const generate = useCallback(
    (engagementMarkdown: string): void => {
      if (!engagementMarkdown.trim()) {
        void message.warning('Engagement context & priorities are required.');
        return;
      }
      if (!selectedGuideSkillValue) {
        void message.warning('Select an interview guide skill.');
        return;
      }
      writeGuideSelection(assessmentInstanceId, selectedGuideSkillValue);
      onRunGenerate();
    },
    [assessmentInstanceId, selectedGuideSkillValue, onRunGenerate],
  );

  return {
    isLoading,
    isSaving,
    assessmentName,
    initialEngagementMarkdown,
    initialStakeholderMarkdown,
    linkedDocCardIds,
    cardModalOpen,
    cardModalId,
    cardModalMode,
    browseOpen,
    guideSkillOptions,
    selectedGuideSkillValue,
    skillsLoadLoading,
    setAssessmentName,
    setSelectedGuideSkillValue,
    linkDocAndOpenModal,
    openModalForCard,
    openCreateNoteModal,
    linkDocAndCloseModal,
    closeCardModal,
    unlinkDoc,
    setBrowseOpen,
    save,
    generate,
  };
}
